// The links /live draws for a view of the graph, from the rail's edges (#111):
//  * satellite: holder → claim, for every claim (a claim is always attached to its holder);
//  * a vouch: attester (from) → claim;
//  * a direct anchor: issuer → claim;
//  * a delegated anchor: the reviewer who signed it (attester_did) → claim, plus one dashed
//    delegation link reviewer → issuer (issuer_did) per reviewer and issuer.
// Nothing is inferred: an anchor without `via` (an older rail) is drawn from `from`.
import type { EdgeKind, LiveEdge, LiveNode } from "./model";

export type LinkKind = EdgeKind | "satellite" | "delegation";
export interface LinkSpec { id: string; kind: LinkKind; source: string; target: string; via?: "delegated" | "direct" }

export function linksOf(view: { nodes: LiveNode[]; edges: LiveEdge[]; claims: [string, string][] }, has?: (id: string) => boolean): LinkSpec[] {
  const dids = new Set(view.nodes.map((n) => n.did));
  const claimIds = new Set(view.claims.map(([c]) => c));
  const ok = has ?? ((id: string) => dids.has(id) || claimIds.has(id));
  const out: LinkSpec[] = [];
  for (const [claimId, holder] of view.claims)
    if (ok(claimId) && ok(holder)) out.push({ id: `s:${claimId}`, kind: "satellite", source: holder, target: claimId });
  const delegations = new Set<string>();
  for (const e of view.edges) {
    if (!ok(e.claim_id)) continue;
    const delegated = e.kind === "anchor" && e.via === "delegated" && e.attester_did && ok(e.attester_did);
    const from = delegated ? e.attester_did! : e.from;
    if (!ok(from)) continue;
    out.push({ id: e.id, kind: e.kind, source: from, target: e.claim_id, ...(e.kind === "anchor" && e.via ? { via: e.via } : {}) });
    const issuer = e.issuer_did ?? e.from;
    if (delegated && ok(issuer)) {
      const id = `d:${from}>${issuer}`;
      if (!delegations.has(id)) { delegations.add(id); out.push({ id, kind: "delegation", source: from, target: issuer }); }
    }
  }
  return out;
}
